import React, { useState, useEffect, Fragment } from "react";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import API from "../../services/API";
import { toast } from "react-toastify";
import moment from "moment";
import {
  FaGripLines,
  FaRegCommentDots,
  FaSchool,
  FaClock,
  FaCheckCircle,
  FaHourglassHalf,
  FaInbox,
  FaPaperPlane,
} from "react-icons/fa";

const laneMeta = {
  pending: {
    title: "Pending",
    subtitle: "New feedback awaiting review",
    icon: <FaInbox />,
    badgeClass: "bg-danger-subtle text-danger",
    accent: "#dc2626",
    soft: "#fff1f2",
    border: "#fecdd3",
  },
  in_progress: {
    title: "In Progress",
    subtitle: "Feedback currently being handled",
    icon: <FaHourglassHalf />,
    badgeClass: "bg-warning-subtle text-warning-emphasis",
    accent: "#f59e0b",
    soft: "#fffbeb",
    border: "#fde68a",
  },
  resolved: {
    title: "Resolved",
    subtitle: "Completed and closed feedback",
    icon: <FaCheckCircle />,
    badgeClass: "bg-success-subtle text-success",
    accent: "#16a34a",
    soft: "#f0fdf4",
    border: "#bbf7d0",
  },
};

const normalizeStatusKey = (status) => {
  if (status === "inProgress") return "in_progress";
  return status;
};

const ComplaintItem = ({ complaint, addComment }) => {
  const [comment, setComment] = useState("");
  const [showReplies, setShowReplies] = useState(false);

  const statusKey = normalizeStatusKey(complaint.status);
  const meta = laneMeta[statusKey] || laneMeta.pending;

  const [{ isDragging }, drag] = useDrag({
    type: "COMPLAINT",
    item: {
      id: complaint.complaint_id,
      status: complaint.status,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleReply = () => {
    const cleanComment = comment.trim();

    if (!cleanComment) {
      toast.error("Please enter a comment");
      return;
    }

    addComment(complaint.complaint_id, cleanComment);
    setComment("");
    setShowReplies(true);
  };

  return (
    <div
      ref={drag}
      className="card border-0 rounded-4 mb-3 overflow-hidden"
      style={{
        opacity: isDragging ? 0.45 : 1,
        cursor: "grab",
        boxShadow: isDragging
          ? "0 20px 50px rgba(15, 23, 42, 0.18)"
          : "0 10px 28px rgba(15, 23, 42, 0.08)",
        transform: isDragging ? "scale(0.98)" : "scale(1)",
        transition: "all 0.18s ease",
      }}
    >
      <div className="card-body p-3">
        <div className="d-flex align-items-start gap-3">
          <div
            className="rounded-4 d-flex align-items-center justify-content-center"
            style={{
              width: 36,
              height: 36,
              background: meta.soft,
              color: meta.accent,
              flex: "0 0 auto",
            }}
          >
            <FaGripLines />
          </div>

          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
              <div
                className="fw-bold text-dark"
                style={{
                  fontSize: 15,
                  lineHeight: 1.35,
                  wordBreak: "break-word",
                }}
              >
                {complaint.description || "No description provided"}
              </div>

              <span
                className={`badge rounded-pill px-2 py-1 ${meta.badgeClass}`}
                style={{ fontSize: 11, whiteSpace: "nowrap" }}
              >
                #{complaint.complaint_id}
              </span>
            </div>

            <div className="d-flex flex-column gap-1 mb-3">
              <div className="d-flex align-items-center gap-2 text-muted small">
                <FaSchool style={{ fontSize: 12 }} />
                <span>{complaint.school_name || "Unknown school"}</span>
              </div>

              <div className="d-flex align-items-center gap-2 text-muted small">
                <FaClock style={{ fontSize: 12 }} />
                <span>
                  {complaint.created_at
                    ? moment(complaint.created_at).format("MMM D, YYYY h:mm A")
                    : "-"}
                </span>
              </div>
            </div>

            <div
              className="rounded-4 p-3"
              style={{
                background: "#f8fafc",
                border: "1px solid #eef2f7",
              }}
            >
              <button
                type="button"
                className="btn btn-sm w-100 rounded-4 fw-semibold d-flex align-items-center justify-content-center gap-2"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  color: "#334155",
                }}
                onClick={() => setShowReplies((prev) => !prev)}
              >
                <FaRegCommentDots />
                {complaint.replies?.length > 0
                  ? showReplies
                    ? "Hide Comments"
                    : `View Comments (${complaint.replies.length})`
                  : "No comments yet"}
              </button>

              {showReplies && complaint.replies?.length > 0 && (
                <div className="mt-3">
                  <div className="d-flex flex-column gap-2">
                    {complaint.replies.map((reply, index) => {
                      const isAdmin = reply.user_role === "admin";

                      return (
                        <div
                          key={index}
                          className={`d-flex ${
                            isAdmin ? "justify-content-start" : "justify-content-end"
                          }`}
                        >
                          <div
                            className="rounded-4 p-3"
                            style={{
                              maxWidth: "92%",
                              background: isAdmin ? "#eff6ff" : "#ffffff",
                              border: isAdmin
                                ? "1px solid #bfdbfe"
                                : "1px solid #e2e8f0",
                            }}
                          >
                            <div
                              className={`fw-bold small mb-1 ${
                                isAdmin ? "text-primary" : "text-dark"
                              }`}
                            >
                              {reply.first_name || "User"}{" "}
                              <span className="text-muted fw-normal">
                                ({isAdmin ? "Admin" : "User"})
                              </span>
                            </div>

                            <div
                              className="text-dark small mb-2"
                              style={{ whiteSpace: "pre-wrap" }}
                            >
                              {reply.comment}
                            </div>

                            <div className="text-muted" style={{ fontSize: 11 }}>
                              {reply.created_at
                                ? moment(reply.created_at).format("MMM D, YYYY h:mm A")
                                : "-"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-3">
                <div className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control form-control-sm rounded-4"
                    placeholder="Write a reply..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleReply();
                    }}
                  />

                  <button
                    type="button"
                    className="btn btn-sm btn-primary rounded-4 px-3"
                    onClick={handleReply}
                    disabled={!comment.trim()}
                  >
                    <FaPaperPlane />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ComplaintLane = ({ status, complaints, moveComplaint, addComment }) => {
  const meta = laneMeta[status];

  const [{ isOver }, drop] = useDrop({
    accept: "COMPLAINT",
    drop: (item) => {
      if (item.status !== status) {
        moveComplaint(item.id, status);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div className="col-12 col-xl-4">
      <div
        ref={drop}
        className="card border-0 rounded-5 h-100 overflow-hidden"
        style={{
          background: "#ffffff",
          boxShadow: isOver
            ? `0 20px 60px ${meta.accent}33`
            : "0 18px 45px rgba(15, 23, 42, 0.08)",
          border: isOver ? `2px solid ${meta.accent}` : "2px solid transparent",
          transition: "all 0.2s ease",
        }}
      >
        <div
          className="p-4"
          style={{
            background: `linear-gradient(135deg, ${meta.soft}, #ffffff)`,
            borderBottom: `1px solid ${meta.border}`,
          }}
        >
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div className="d-flex align-items-start gap-3">
              <div
                className="rounded-4 d-flex align-items-center justify-content-center"
                style={{
                  width: 46,
                  height: 46,
                  background: "#ffffff",
                  color: meta.accent,
                  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
                  flex: "0 0 auto",
                }}
              >
                {meta.icon}
              </div>

              <div>
                <h5 className="fw-black text-dark mb-1">{meta.title}</h5>
                <div className="text-muted small">{meta.subtitle}</div>
              </div>
            </div>

            <span
              className={`badge rounded-pill px-3 py-2 ${meta.badgeClass}`}
              style={{ fontSize: 12 }}
            >
              {complaints.length}
            </span>
          </div>
        </div>

        <div
          className="p-3"
          style={{
            minHeight: 520,
            maxHeight: "calc(100vh - 270px)",
            overflowY: "auto",
            background: isOver ? meta.soft : "#f8fafc",
            transition: "background 0.2s ease",
          }}
        >
          {complaints.length === 0 ? (
            <div
              className="rounded-5 border text-center p-5"
              style={{
                background: "#ffffff",
                borderStyle: "dashed",
                borderColor: "#cbd5e1",
              }}
            >
              <div
                className="mx-auto rounded-circle d-flex align-items-center justify-content-center mb-3"
                style={{
                  width: 58,
                  height: 58,
                  background: meta.soft,
                  color: meta.accent,
                  fontSize: 22,
                }}
              >
                {meta.icon}
              </div>

              <div className="fw-bold text-dark mb-1">No feedback here</div>
              <div className="text-muted small">
                Drag a card here to update its status.
              </div>
            </div>
          ) : (
            complaints.map((complaint) => (
              <ComplaintItem
                key={complaint.complaint_id}
                complaint={complaint}
                moveComplaint={moveComplaint}
                addComment={addComment}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const ComplainPage = () => {
  const [complaints, setComplaints] = useState({
    pending: [],
    inProgress: [],
    resolved: [],
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchComplaints();
  }, []);

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    token: localStorage.getItem("token"),
  });

  const fetchComplaints = async () => {
    setLoading(true);

    const headers = getHeaders();

    try {
      const response = await API.post(
        "/complaint/getComplaintsAsAdmin",
        {},
        { headers }
      );

      if (response.data.response_code === 1000) {
        const allComplaints = response.data.complaints || [];

        const complaintsWithReplies = await Promise.all(
          allComplaints.map(async (complaint) => {
            try {
              const replyRes = await API.post(
                "/complaint/getComplaintReplies",
                { complaint_id: complaint.complaint_id },
                { headers }
              );

              if (replyRes.data.response_code === 1000) {
                return {
                  ...complaint,
                  replies: replyRes.data.replies || [],
                };
              }

              return {
                ...complaint,
                replies: [],
              };
            } catch {
              return {
                ...complaint,
                replies: [],
              };
            }
          })
        );

        const pending = complaintsWithReplies.filter(
          (c) => c.status?.toLowerCase() === "pending"
        );

        const inProgress = complaintsWithReplies.filter(
          (c) => c.status?.toLowerCase() === "in_progress"
        );

        const resolved = complaintsWithReplies.filter(
          (c) => c.status?.toLowerCase() === "resolved"
        );

        setComplaints({
          pending,
          inProgress,
          resolved,
        });
      } else {
        toast.error(response.data.message || "Failed to fetch complaints", {
          autoClose: 5000,
        });
      }
    } catch {
      toast.error("Error fetching complaints", { autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const moveComplaint = (id, newStatus) => {
    let oldStateBackup = null;

    setComplaints((prevState) => {
      oldStateBackup = prevState;

      const updatedComplaints = {
        pending: [...prevState.pending],
        inProgress: [...prevState.inProgress],
        resolved: [...prevState.resolved],
      };

      let movedComplaint = null;

      for (const key in updatedComplaints) {
        const index = updatedComplaints[key].findIndex(
          (c) => c.complaint_id === id
        );

        if (index !== -1) {
          movedComplaint = updatedComplaints[key].splice(index, 1)[0];
          break;
        }
      }

      if (movedComplaint) {
        movedComplaint.status = newStatus;

        if (newStatus === "pending") {
          updatedComplaints.pending.push(movedComplaint);
        }

        if (newStatus === "in_progress") {
          updatedComplaints.inProgress.push(movedComplaint);
        }

        if (newStatus === "resolved") {
          updatedComplaints.resolved.push(movedComplaint);
        }
      }

      return updatedComplaints;
    });

    const headers = getHeaders();

    API.post(
      "/complaint/updateComplaintStatusAsAdmin",
      {
        complaint_id: id,
        status: newStatus,
      },
      { headers }
    )
      .then((response) => {
        if (response.data.response_code === 1000) {
          toast.success("Complaint status updated successfully");
        } else {
          if (oldStateBackup) setComplaints(oldStateBackup);
          toast.error(response.data.message || "Failed to update status", {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        if (oldStateBackup) setComplaints(oldStateBackup);
        toast.error("Error updating feedback status", { autoClose: 5000 });
      });
  };

  const addComment = (complaint_id, comment) => {
    if (!comment?.trim()) return;

    const headers = getHeaders();

    API.post(
      "/complaint/adminReplyComplaint",
      {
        complaint_id,
        comment,
      },
      { headers }
    )
      .then((response) => {
        if (response.data.response_code === 1000) {
          toast.success("Comment added successfully");
          fetchComplaints();
        } else {
          toast.error(response.data.message || "Failed to add comment", {
            autoClose: 5000,
          });
        }
      })
      .catch(() => {
        toast.error("Error adding comment", { autoClose: 5000 });
      });
  };

  const totalComplaints =
    complaints.pending.length +
    complaints.inProgress.length +
    complaints.resolved.length;

  return (
    <DndProvider backend={HTML5Backend}>
      <Fragment>
        <div
          className="container-fluid py-4"
          style={{
            background: "#f8fafc",
            minHeight: "100vh",
          }}
        >
          <div
            className="rounded-5 p-4 p-md-5 mb-4 position-relative overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #111827 0%, #1e293b 48%, #dc2626 100%)",
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
            }}
          >
            <div
              className="position-absolute top-0 end-0 rounded-circle"
              style={{
                width: 260,
                height: 260,
                background: "rgba(255,255,255,0.10)",
                transform: "translate(30%, -35%)",
              }}
            />

            <div
              className="position-absolute bottom-0 start-50 rounded-circle"
              style={{
                width: 180,
                height: 180,
                background: "rgba(255,255,255,0.08)",
                transform: "translate(-50%, 45%)",
              }}
            />

            <div className="position-relative d-flex flex-column flex-lg-row justify-content-between gap-4">
              <div>
                <div
                  className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-3"
                  style={{
                    background: "rgba(255,255,255,0.14)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.20)",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  <span
                    className="rounded-circle"
                    style={{
                      width: 8,
                      height: 8,
                      background: "#22c55e",
                      display: "inline-block",
                    }}
                  />
                  Feedback Operations
                </div>

                <h1
                  className="text-white fw-black mb-2"
                  style={{
                    fontSize: "clamp(32px, 4vw, 52px)",
                    letterSpacing: "-1.5px",
                  }}
                >
                  Complaint Management
                </h1>

                <p
                  className="text-white-50 mb-0"
                  style={{
                    maxWidth: 720,
                    fontSize: 16,
                  }}
                >
                  Track parent and school feedback across pending, in-progress,
                  and resolved stages. Drag cards between lanes to update status.
                </p>
              </div>

              <div className="d-flex flex-wrap align-items-start gap-3">
                <div
                  className="rounded-4 px-4 py-3 text-white"
                  style={{
                    background: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.20)",
                    backdropFilter: "blur(10px)",
                    minWidth: 150,
                  }}
                >
                  <div className="text-white-50 small">Total Feedback</div>
                  <div className="fw-black fs-3">{totalComplaints}</div>
                </div>

                <button
                  type="button"
                  className="btn border-0 rounded-4 fw-bold px-4 py-3"
                  style={{
                    background: "#ffffff",
                    color: "#991b1b",
                    boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
                  }}
                  onClick={fetchComplaints}
                  disabled={loading}
                >
                  {loading ? "Refreshing..." : "Refresh"}
                </button>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card border-0 rounded-5 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="text-muted small fw-semibold mb-2">Pending</div>
                  <div className="display-6 fw-black text-danger">
                    {complaints.pending.length}
                  </div>
                  <div className="text-muted small mt-2">
                    Feedback awaiting admin review
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 rounded-5 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="text-muted small fw-semibold mb-2">
                    In Progress
                  </div>
                  <div className="display-6 fw-black text-warning">
                    {complaints.inProgress.length}
                  </div>
                  <div className="text-muted small mt-2">
                    Feedback being investigated
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 rounded-5 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="text-muted small fw-semibold mb-2">Resolved</div>
                  <div className="display-6 fw-black text-success">
                    {complaints.resolved.length}
                  </div>
                  <div className="text-muted small mt-2">
                    Feedback closed successfully
                  </div>
                </div>
              </div>
            </div>
          </div>

          {loading && totalComplaints === 0 ? (
            <div className="card border-0 rounded-5 shadow-sm">
              <div className="card-body text-center p-5">
                <div className="spinner-border text-danger mb-3" role="status" />
                <div className="fw-bold text-dark">Loading complaints</div>
                <div className="text-muted small">
                  Please wait while we fetch the feedback board.
                </div>
              </div>
            </div>
          ) : (
            <div className="row g-4">
              <ComplaintLane
                status="pending"
                complaints={complaints.pending}
                moveComplaint={moveComplaint}
                addComment={addComment}
              />

              <ComplaintLane
                status="in_progress"
                complaints={complaints.inProgress}
                moveComplaint={moveComplaint}
                addComment={addComment}
              />

              <ComplaintLane
                status="resolved"
                complaints={complaints.resolved}
                moveComplaint={moveComplaint}
                addComment={addComment}
              />
            </div>
          )}
        </div>
      </Fragment>
    </DndProvider>
  );
};

export default ComplainPage;